import React, { useState } from 'react';
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { FaBolt, FaExclamationTriangle, FaSync } from "react-icons/fa";
import Spinner from "./Spinner";
import NotFound from "../pages/NotFound";
import AuditSymbolCompliance from "./AuditSymbolCompliance";
import DetailTabs from "../components/DetailTabs";
import EditLocationModal from "./modals/EditLocationModal";
import DeviceAssignmentModal from "./modals/DeviceAssignmentModal";
import AssignUserModal from "./modals/AssignUserModal";
import isAdministrator, { isStaff } from "../utilities/checkPrivileges";
import { deviceLastSeenLabel, deviceDdmReportLabel, deviceDdmReportMs } from "../utilities/deviceLastSeen.js";
import { filterActionButtons, getDeviceCapabilities } from "../utilities/deviceCapabilities.js";
import DeviceNightRestStatus from "./DeviceNightRestStatus";

function classroomProfileLabel(device) {
  const rows = (device?.Profiles || []).filter((profile) => {
    const identifier = String(profile?.PayloadIdentifier || "").toLowerCase();
    const nested = (profile?.PayloadContent || []).some(
      (item) => String(item?.PayloadType || "") === "com.apple.education"
    );
    return identifier.startsWith("de.xavier.mdm.education")
      || nested
      || /^classroom\b/i.test(profile?.PayloadDisplayName || "");
  });
  if (!rows.length) {
    return "nicht im Inventar — Profile unter dem Tab „Profiles“ prüfen, nach Ausspielen Inventar aktualisieren";
  }
  return rows.map((profile) => profile.PayloadDisplayName || profile.PayloadIdentifier).join(", ");
}

const ASSIGNMENT_ACTIONS = [
  { label: "Gruppe zuweisen", tab: "groups" },
  { label: "App zuweisen", tab: "apps" },
  { label: "DDM zuweisen", tab: "ddm" },
];

function shouldShowDeviceDetailDebug() {
  if (typeof window === "undefined") return false;
  try {
    if (process.env.NODE_ENV === "development") return true;
    return new URLSearchParams(window.location.search).get("deviceDebug") === "1";
  } catch {
    return false;
  }
}

/**
 * Detailliertes Fehler-Logging: nur in Development oder mit ?deviceDebug=1
 */
function DeviceQueryDebug({ error, dataSample, context }) {
  if (!shouldShowDeviceDetailDebug()) return null;
  const lines = [];
  if (context) lines.push("Kontext:", JSON.stringify(context, null, 2), "");
  if (error) {
    lines.push("Apollo/GraphQL:", error.message);
    if (error.graphQLErrors?.length) {
      lines.push(
        "graphQLErrors:",
        ...error.graphQLErrors.map(
          (e) =>
            `  - ${e.message} | path: ${JSON.stringify(e.path)} | extensions: ${JSON.stringify(
              e.extensions
            )}`
        )
      );
    }
    if (error.networkError) {
      const n = error.networkError;
      lines.push(
        "networkError:",
        n.message,
        n.statusCode != null ? `statusCode: ${n.statusCode}` : "",
        n.result ? `body: ${JSON.stringify(n.result).slice(0, 2000)}` : ""
      );
    }
  }
  if (dataSample != null) {
    lines.push("Antwort (vereinfacht, für Null-Prüfung):", dataSample);
  }
  return (
    <div
      className="alert alert-warning text-start mt-3 mx-auto"
      style={{ maxWidth: 960, fontSize: "0.85rem" }}
    >
      <strong>Device-Detail-Debug</strong> (Nur sichtbar mit ?deviceDebug=1 oder im Development-Build.)
      <pre
        className="mb-0 mt-2 p-2 bg-light border rounded"
        style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}
      >
        {lines.filter(Boolean).join("\n")}
      </pre>
    </div>
  );
}

/**
 * Sichtbar bei GraphQL-Teilfehlern, wenn trotzdem ein Geräte-Datensatz geladen wurde
 */
function DeviceQueryPartialErrorBanner({ error }) {
  if (!error?.graphQLErrors?.length) return null;
  return (
    <div className="alert alert-warning" role="alert" style={{ maxWidth: 960, margin: "0 auto" }}>
      <strong className="d-block">Einige Felder liefern Fehler (GraphQL)</strong>
      <p className="mb-1 small text-muted">
        Die Seite nutzt trotzdem die vorhandenen Daten. Details für Support / Debugging:
      </p>
      <ul className="mb-0 small">
        {error.graphQLErrors.map((e, i) => (
          <li key={i}>
            <code className="text-break">{e.message}</code>
            {e.path?.length ? (
              <span className="text-muted"> (Pfad: {e.path.join(" → ")})</span>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Renders the header section of the device detail page
 */
const DeviceHeader = ({ deviceName, lastCheckin, ddmReport, enrollmentLabel, showActions, actionButtons, device, onRefresh, isRefreshing, onAssignmentAction }) => (
  <div className='header'>
    <div>
      <h1>{deviceName}</h1>
      <h6>Last seen {lastCheckin}</h6>
      {ddmReport ? <h6 className="text-muted mb-0">DDM-Report {ddmReport}</h6> : null}
      {enrollmentLabel ? <h6 className="text-muted mb-0">{enrollmentLabel}</h6> : null}
    </div>
    {(showActions || onRefresh) && (
      <div className='d-flex gap-2 align-items-center'>
        {onRefresh && (
          <button
            className='btn btn-outline-secondary btn-sm'
            type='button'
            onClick={() => onRefresh(device)}
            disabled={isRefreshing}
            title='Inventur aktualisieren'
            aria-label='Inventur aktualisieren'
          >
            <FaSync className={isRefreshing ? 'spin' : ''} />
          </button>
        )}
        {showActions && (
          <div className='dropdown'>
            <button
              className='btn dropdown-toggle'
              type='button'
              data-bs-toggle='dropdown'
              aria-expanded='false'
            >
              <FaBolt />
            </button>
            <ul className='dropdown-menu hide'>
              {ASSIGNMENT_ACTIONS.map((action) => (
                <li key={action.tab}>
                  <button
                    className='dropdown-item'
                    onClick={() => onAssignmentAction(action.tab)}
                  >
                    {action.label}
                  </button>
                </li>
              ))}
              {actionButtons?.length > 0 && <li><hr className="dropdown-divider" /></li>}
              {actionButtons.map((action, index) => (
                <li key={index}>
                  <button
                    className='dropdown-item'
                    onClick={() => action.onClick(device)}
                    title={action.title || undefined}
                  >
                    {action.getLabel ? action.getLabel(device) : action.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    )}
  </div>
);

/**
 * Renders a section of device information in a table format
 */
const DeviceInfoSection = ({ title, data }) => (
  <div className='col col-sm-12 col-md-6 col-xl-4'>
    <h5>{title}</h5>
    <div className='p-3 border bg-light' style={{ maxHeight: '28rem', overflow: 'auto' }}>
      <table className='table mb-0'>
        <tbody>
          {Object.entries(data).map(([key, value]) => {
            if (value === undefined) return null;
            return (
              <tr key={key}>
                <td className="text-nowrap">{key}</td>
                <td style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                  {typeof value === 'boolean' ? (
                    <AuditSymbolCompliance status={value} />
                  ) : (
                    value
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
);

/**
 * Base component for device detail pages (Mac, iPhone, iPad, Apple TV)
 * @param {Object} props
 * @param {Object} props.query - GraphQL query for fetching device data
 * @param {string} props.deviceType - Type of device ('mac', 'iphone', 'ipad', 'appletv')
 * @param {Function} props.getDeviceData - Function to extract device data from query response
 * @param {Array} props.actionButtons - Array of action button configurations
 * @param {Function} props.renderModals - Function to render device-specific modals
 * @param {Array} props.infoSections - Array of info section configurations
 */
export const DeviceDetailBase = ({ 
  query, 
  deviceType,
  getDeviceData,
  actionButtons,
  renderModals,
  infoSections,
  onRefresh
}) => {
  const { deviceId, SerialNumber } = useParams();
  const resolvedDeviceId = deviceId || SerialNumber;
  const [showEditLocationModal, setShowEditLocationModal] = useState(false);
  const [assignmentTab, setAssignmentTab] = useState(null);
  const [showAssignUser, setShowAssignUser] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  const { loading, error, data, refetch } = useQuery(query, {
    variables: { SerialNumber: resolvedDeviceId, UDID: resolvedDeviceId },
    // Teildaten trotz Feldfehlern (z. B. ungültige Int-Werte) anzeigen
    errorPolicy: "all",
  });

  const schedulePostActionRefetch = () => {
    // Command-Antworten und NanoCMD-Inventar kommen zeitversetzt an.
    [2000, 8000, 20000, 45000].forEach((delayMs) => {
      window.setTimeout(() => {
        refetch();
      }, delayMs);
    });
  };

  const handleRefresh = async (device) => {
    if (!onRefresh || !device) return;
    setIsRefreshing(true);
    try {
      await onRefresh(device);
      schedulePostActionRefetch();
    } finally {
      setTimeout(() => setIsRefreshing(false), 2500);
    }
  };

  if (loading) return <Spinner />;

  const device = data ? getDeviceData(data) : null;

  if (!device) {
    if (error && !data) {
      return (
        <>
          <NotFound />
          <DeviceQueryDebug
            error={error}
            context={{ deviceType, resolvedDeviceId, cause: "graphql_or_network" }}
          />
        </>
      );
    }
    return (
      <>
        <div className="d-flex flex-column justify-content-center align-items-center mt-5">
          <FaExclamationTriangle className="text-warning" size="5em" />
          <h1 className="h3 mt-3">Gerät nicht gefunden</h1>
          <p className="lead text-center px-3">
            Zu dieser ID wurde kein Eintrag in der Datenbank gefunden. Die Adresse nutzt in der
            Regel entweder die Seriennummer oder die UDID; wenn nur eines davon in der
            Weboberfläche verlinkt ist, muss es mit dem Profil in der URL übereinstimmen.
          </p>
          <Link to="/" className="btn btn-primary">
            Zur Startseite
          </Link>
        </div>
        <DeviceQueryDebug
          dataSample={JSON.stringify(
            { queryTopLevelKeys: data ? Object.keys(data) : [] },
            null,
            2
          )}
          context={{ deviceType, resolvedDeviceId, cause: "no_device_row" }}
        />
      </>
    );
  }
  const lastCheckin = deviceLastSeenLabel(device);
  const ddmReport = deviceDdmReportMs(device) ? deviceDdmReportLabel(device) : null;

  const locationLabel = device.location
    ? `${device.location.schoolNumber || ''} – ${device.location.name || ''}`.trim()
    : '—';
  const assetTagLabel = device.assetTag || '—';

  const organizationSection = {
    title: 'organization',
    getData: (d) => {
      const groups = (d.assignedDeviceGroups || [])
        .map((g) => g.groupName)
        .filter(Boolean);
      const groupsLabel = groups.length
        ? groups.join(', ')
        : '—';
      const assigned = d.assignedDirectoryUser;
      const assignedLabel = assigned
        ? `${assigned.name}${assigned.className ? ` · ${assigned.className}` : ''}`
        : '—';
      return {
        'location': isAdministrator() ? (
          <span>
            {locationLabel}
            {' '}
            <button
              type="button"
              className="btn btn-link btn-sm p-0 align-baseline"
              onClick={() => setShowEditLocationModal(true)}
            >
              bearbeiten
            </button>
          </span>
        ) : locationLabel,
        'asset tag': assetTagLabel,
        'assigned user': isStaff() ? (
          <span>
            {assignedLabel}
            {' '}
            <button
              type="button"
              className="btn btn-link btn-sm p-0 align-baseline"
              onClick={() => setShowAssignUser(true)}
            >
              bearbeiten
            </button>
          </span>
        ) : assignedLabel,
        'Classroom': classroomProfileLabel(d),
        'device groups': isAdministrator() ? (
          <span>
            {groupsLabel}
            {' '}
            <button
              type="button"
              className="btn btn-link btn-sm p-0 align-baseline"
              onClick={() => setAssignmentTab('groups')}
            >
              bearbeiten
            </button>
          </span>
        ) : groupsLabel,
        ...((deviceType === 'iphone' || deviceType === 'ipad') ? {
          'Nachtruhe': <DeviceNightRestStatus udid={d.UDID} />,
        } : {}),
      };
    },
  };

  const allSections = [organizationSection, ...infoSections];
  const caps = getDeviceCapabilities(device);
  const visibleActions = filterActionButtons(device, actionButtons);

  return (
    <>
      <main className='container main-content'>
          <DeviceQueryPartialErrorBanner error={error} />
          <DeviceHeader 
            deviceName={device.QueryResponses.DeviceName}
            lastCheckin={lastCheckin}
            ddmReport={ddmReport}
            enrollmentLabel={caps.enrollmentLabel}
            showActions={device.mdmProfileInstalled && isAdministrator()}
            actionButtons={visibleActions}
            device={device}
            onAssignmentAction={setAssignmentTab}
            onRefresh={(device.mdmProfileInstalled && isAdministrator()) ? handleRefresh : undefined}
            isRefreshing={isRefreshing}
          />

          <div className='row gx-5'>
            {allSections.map((section, index) => (
              <DeviceInfoSection
                key={index}
                title={section.title}
                data={section.getData(device)}
              />
            ))}
          </div>

          <hr />
          <DetailTabs device={device} onDataChanged={schedulePostActionRefetch} />
          {renderModals && renderModals(device, data?.configProfiles, schedulePostActionRefetch)}
          {isStaff() && (
            <>
              <AssignUserModal
                visible={showAssignUser}
                device={device}
                udid={device.UDID}
                serialNumber={device.SerialNumber}
                currentUser={device.assignedDirectoryUser}
                onClose={() => setShowAssignUser(false)}
                onSaved={() => refetch()}
              />
            </>
          )}
          {isAdministrator() && (
            <>
              <EditLocationModal
                visible={showEditLocationModal}
                device={device}
                deviceType={deviceType}
                onClose={() => setShowEditLocationModal(false)}
                onSuccess={() => refetch()}
              />
              <DeviceAssignmentModal
                visible={Boolean(assignmentTab)}
                device={device}
                initialTab={assignmentTab || "groups"}
                onClose={() => setAssignmentTab(null)}
                onSaved={() => refetch()}
              />
            </>
          )}
      </main>
    </>
  );
};

/**
 * Utility function to calculate storage percentage
 */
export const calculateStoragePercentage = (device) => {
  return `${Math.round(
    (device.QueryResponses.AvailableDeviceCapacity /
      device.QueryResponses.DeviceCapacity) *
      100
  )}% free of ${device.QueryResponses.DeviceCapacity} GB`;
};

/**
 * Utility function to format MAC addresses
 */
export const formatMacAddress = (mac) => {
  return mac ? mac.toUpperCase() : undefined;
};

export default DeviceDetailBase;
